// RichTextToolbar — the formatting button row factored out of
// RichTextEditor so it can sit above a contentEditable surface that
// component doesn't own.
//
// Why this exists (Nate Swartz, 2026-08-28): the automation builder's
// body field got bold/italic/underline/lists/link on 2026-07-22 (Grant's
// "does not allow me to insert a hyperlink" report), but the 1:1 record
// composer (EmailComposer) and the inline reply composer never did —
// both are plain contentEditable divs with no toolbar at all, which is
// why a pasted form link in Nate's screenshot rendered as raw text
// instead of a clickable link.
//
// RichTextEditor can't just be dropped into those composers: it's a
// fully controlled component that sanitizes its value down to a small
// tag allowlist on every change (shared/email/rich-text-allowlist.ts),
// and both composers seed their body with the advisor's pasted Outlook
// signature — tables, inline styles, an image badge — which that
// allowlist would strip to plain text. So this toolbar operates against
// whatever contentEditable ref its caller already owns (uncontrolled),
// and leaves sanitizing to the caller. RichTextEditor itself is
// refactored to render this component too, so the button list and
// exec() logic exist in exactly one place.

import { useCallback, useEffect, useState } from "react";
import type { KeyboardEvent, ReactNode, RefObject } from "react";

import { CatalogIcon } from "./icon-map";
import {
  LINK_URL_REJECTED_MESSAGE,
  RICH_TEXT_TOOLBAR,
  type RichTextCommand,
  normalizeLinkUrl,
} from "./rich-text-editor-helpers";

/** execCommand is untyped in lib.dom for the commands we use, and
 *  throws in environments without a document selection. Every call is
 *  best-effort: a command that can't apply must never break typing. */
function exec(command: string, value?: string): void {
  try {
    document.execCommand(command, false, value);
  } catch {
    // Formatting is a nicety; losing one click must not break the field.
  }
}

function escapeAttribute(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function escapeText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Ctrl/Cmd+B/I/U run the browser's native formatting, but execCommand
 *  fires without a React event — call this from the surface's onKeyDown
 *  so the host's change tracking (autosave, dirty state, …) notices the
 *  formatting the shortcut just applied. Same shape as the toolbar
 *  buttons' own applyCommand, which calls onChange after exec rather
 *  than relying on a native "input" event. */
export function handleRichTextKeyDown(
  e: KeyboardEvent<HTMLElement>,
  onChange: () => void,
): void {
  const mod = e.metaKey || e.ctrlKey;
  if (mod && ["b", "i", "u"].includes(e.key.toLowerCase())) {
    setTimeout(onChange, 0);
  }
}

export interface RichTextToolbarProps {
  /** The contentEditable surface this toolbar formats. Owned by the
   *  caller, not this component. */
  editorRef: RefObject<HTMLDivElement | null>;
  /** Called after every command, link insert/remove — the host reads
   *  editorRef.current.innerHTML off its own ref and does whatever it
   *  does with a body change (autosave, dirty tracking, sanitize +
   *  store, …). */
  onChange: () => void;
  /** Rendered after the link buttons — e.g. EmailComposer's "Insert a
   *  form" button. Kept as a slot rather than a prop this component
   *  interprets, so it stays agnostic of what a caller wants there. */
  extraButtons?: ReactNode;
  /** Disables every button — for a composer mid-send, matching the
   *  disabled state its other controls already carry. */
  disabled?: boolean;
}

export function RichTextToolbar({
  editorRef,
  onChange,
  extraButtons,
  disabled = false,
}: RichTextToolbarProps) {
  const [linkError, setLinkError] = useState<string | null>(null);
  // Re-render trigger for the toolbar's active states, so Bold looks
  // pressed when the caret sits inside bold text.
  const [, setSelectionTick] = useState(0);

  useEffect(() => {
    function onSelectionChange() {
      const el = editorRef.current;
      if (!el) return;
      const sel = document.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      if (!el.contains(sel.anchorNode)) return;
      setSelectionTick((n) => n + 1);
    }
    document.addEventListener("selectionchange", onSelectionChange);
    return () =>
      document.removeEventListener("selectionchange", onSelectionChange);
  }, [editorRef]);

  const focusEditor = useCallback(() => {
    editorRef.current?.focus();
  }, [editorRef]);

  const applyCommand = useCallback(
    (command: RichTextCommand) => {
      focusEditor();
      exec(command);
      onChange();
    },
    [onChange, focusEditor],
  );

  const insertLink = useCallback(() => {
    focusEditor();
    setLinkError(null);
    // Snapshot the selected range BEFORE opening the prompt. window.prompt
    // is a browser-chrome modal that blurs the editor (firing our onBlur),
    // and some browsers collapse the selection on that blur — so
    // "select text → Insert link" would apply createLink to nothing.
    // Cloning the range and restoring it after the prompt makes the
    // wrap-the-selection path reliable.
    const sel = document.getSelection();
    const savedRange =
      sel && sel.rangeCount > 0 && !sel.isCollapsed
        ? sel.getRangeAt(0).cloneRange()
        : null;
    // eslint-disable-next-line no-alert -- a modal dialog for one string
    // is more machinery than this earns; window.prompt is the same
    // affordance the browser uses for its own link UI.
    const raw = window.prompt("Link address", "https://");
    if (raw === null) return;
    const url = normalizeLinkUrl(raw);
    if (!url) {
      setLinkError(LINK_URL_REJECTED_MESSAGE);
      return;
    }
    focusEditor();
    if (savedRange) {
      const s = document.getSelection();
      s?.removeAllRanges();
      s?.addRange(savedRange);
      exec("createLink", url);
    } else {
      // No selection means there's nothing to turn into a link, so insert
      // the address as its own linked text rather than silently no-oping.
      exec("insertHTML", `<a href="${escapeAttribute(url)}">${escapeText(url)}</a>`);
    }
    onChange();
  }, [onChange, focusEditor]);

  const removeLink = useCallback(() => {
    focusEditor();
    exec("unlink");
    onChange();
  }, [onChange, focusEditor]);

  function isActive(command: RichTextCommand): boolean {
    try {
      return document.queryCommandState(command);
    } catch {
      return false;
    }
  }

  return (
    <>
      <div className="rt-editor__toolbar" role="toolbar" aria-label="Formatting">
        {RICH_TEXT_TOOLBAR.map((btn) => (
          <button
            key={btn.command}
            type="button"
            className={`rt-editor__tool${isActive(btn.command) ? " rt-editor__tool--on" : ""}`}
            aria-label={btn.label}
            aria-pressed={isActive(btn.command)}
            title={btn.shortcut ? `${btn.label} (${btn.shortcut})` : btn.label}
            disabled={disabled}
            // onMouseDown, not onClick: the default mousedown would blur
            // the editor and collapse the selection before the command
            // ran, so "select text → click Bold" would do nothing.
            onMouseDown={(e) => {
              e.preventDefault();
              applyCommand(btn.command);
            }}
          >
            <CatalogIcon name={btn.icon} size={14} />
          </button>
        ))}
        <span className="rt-editor__tool-sep" aria-hidden />
        <button
          type="button"
          className="rt-editor__tool"
          aria-label="Insert link"
          title="Insert link"
          disabled={disabled}
          onMouseDown={(e) => {
            e.preventDefault();
            insertLink();
          }}
        >
          <CatalogIcon name="Link2" size={14} />
        </button>
        <button
          type="button"
          className="rt-editor__tool"
          aria-label="Remove link"
          title="Remove link"
          disabled={disabled}
          onMouseDown={(e) => {
            e.preventDefault();
            removeLink();
          }}
        >
          <CatalogIcon name="Link2Off" size={14} />
        </button>
        {extraButtons}
      </div>
      {linkError && (
        <p className="rt-editor__error" role="status">
          {linkError}
        </p>
      )}
    </>
  );
}
